import { useEffect, useState } from "react";
import { FibiLoading } from "../components/FibiLoading";
import { Link } from "react-router-dom";
import { ArrowLeft, Network, RefreshCcw, Wifi } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useRouters } from "../context/RouterContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const fmtBytes = (b) => {
  const n = Number(b || 0);
  if (n >= 1073741824) return `${(n / 1073741824).toFixed(2)} GB`;
  if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  return `${(n / 1024).toFixed(0)} KB`;
};

export default function OnlineUsers({ type }) {
  const isHs = type === "hotspot";
  const { selected, router } = useRouters();
  const [items, setItems] = useState(null);

  const load = () => {
    if (!selected) return;
    api.get(`/routers/${selected}/${isHs ? "hotspot" : "ppp"}/online`)
      .then((r) => setItems(r.data))
      .catch((e) => { setItems([]); toast.error(errMsg(e)); });
  };

  useEffect(() => {
    setItems(null);
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  return (
    <div className="space-y-6" data-testid="online-users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            {isHs ? <Wifi className="w-6 h-6 text-blue-600" /> : <Network className="w-6 h-6 text-rose-500" />}
            {isHs ? "Hotspot Online" : "PPP Online"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isHs ? "User hotspot yang sedang login" : "Koneksi PPPoE yang sedang aktif"} di {router?.name || "router"} — data live dari MikroTik.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="btn-refresh-online"><RefreshCcw className="w-4 h-4 mr-1.5" /> Muat Ulang</Button>
          <Link to="/"><Button variant="ghost" data-testid="btn-back-dashboard"><ArrowLeft className="w-4 h-4 mr-1.5" /> Dashboard</Button></Link>
        </div>
      </div>

      <Card className="bg-card border-border shadow-sm overflow-x-auto" data-testid="online-users-card">
        <CardHeader><CardTitle className="text-base font-heading">{isHs ? "Daftar Hotspot Online" : "Daftar PPP Online"}{items ? ` (${items.length})` : ""}</CardTitle></CardHeader>
        <Table className="min-w-[760px]" data-testid="online-users-table">
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              {isHs ? (
                <>
                  <TableHead>User</TableHead><TableHead>IP Address</TableHead><TableHead>MAC Address</TableHead>
                  <TableHead>Uptime</TableHead><TableHead>Download</TableHead><TableHead>Upload</TableHead><TableHead>Login By</TableHead>
                </>
              ) : (
                <>
                  <TableHead>Nama</TableHead><TableHead>Service</TableHead><TableHead>Caller ID</TableHead>
                  <TableHead>IP Address</TableHead><TableHead>Uptime</TableHead>
                </>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items === null && <TableRow><TableCell colSpan={8} className="p-0"><FibiLoading /></TableCell></TableRow>}
            {items !== null && !items.length && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground" data-testid="online-empty">Tidak ada yang online saat ini</TableCell></TableRow>}
            {(items || []).map((a, i) => (
              <TableRow key={`${isHs ? a.user : a.name}-${i}`} data-testid={`online-row-${isHs ? a.user : a.name}`}>
                <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                {isHs ? (
                  <>
                    <TableCell className="font-medium font-mono">{a.user}</TableCell>
                    <TableCell className="font-mono text-sm">{a.address || "-"}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{a.mac || "-"}</TableCell>
                    <TableCell className="font-mono text-sm">{a.uptime || "-"}</TableCell>
                    <TableCell className="font-mono text-sm text-rose-500">{fmtBytes(a.bytes_in)}</TableCell>
                    <TableCell className="font-mono text-sm text-sky-500">{fmtBytes(a.bytes_out)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{a.login_by || "-"}</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell className="font-medium font-mono">{a.name}</TableCell>
                    <TableCell><span className="px-2 py-0.5 rounded text-[11px] font-mono bg-sky-500/10 text-sky-400 border border-sky-500/20">{a.service || "-"}</span></TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{a.caller_id || "-"}</TableCell>
                    <TableCell className="font-mono text-sm">{a.address || "-"}</TableCell>
                    <TableCell className="font-mono text-sm">{a.uptime || "-"}</TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
